'use client'

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Play, Zap, AlertTriangle, ExternalLink } from 'lucide-react'
import { ModelTestDialog } from './model-test-dialog'
import type { AIModel, ModelFormState, ModelType } from './types'
import { formatMicroUsdPrecise } from '@/lib/billing/utils/format'
import { isPricedCost } from '@/lib/ai/model-cost'
import { getProviderDocLinks } from '../provider-doc-links'
import { ParamKeys } from '../param-keys'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






interface ModelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: ModelFormState
  onChange: (next: ModelFormState) => void
  onSave: (opts?: { keepOpen?: boolean }) => Promise<boolean>
  onDelete?: () => Promise<void> | void
  isSaving: boolean
  editingModel: AIModel | null
  /** Human-facing provider name (display), used in headings. */
  providerName: string
  /** Canonical provider key ('openai' | 'anthropic' | 'google') for provider-specific logic. */
  providerKey: string
  providerId: string
}

function getMODEL_TYPES(tUi: ReturnType<typeof useTranslations<"Ai.AiProvidersModelDialog">>): { value: ModelType; label: string }[] {
  return [
  { value: 'chat', label: tUi("chatCompletion") },
  { value: 'embedding', label: tUi("embedding") },
  { value: 'image', label: tUi("imageGeneration") },
  { value: 'audio', label: tUi("audioSpeech") },
]
}

export function ModelDialog({
  open,
  onOpenChange,
  formData,
  onChange,
  onSave,
  onDelete,
  isSaving,
  editingModel,
  providerName,
  providerKey,
  providerId,
}: ModelDialogProps) {



  const tUi = useTranslations("Ai.AiProvidersModelDialog")
  const MODEL_TYPES = getMODEL_TYPES(tUi)

  const tCommonUi = useTranslations("Common")

  const [testDialogOpen, setTestDialogOpen] = useState(false)
  const [isPreparingTest, setIsPreparingTest] = useState(false)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  // Zero does not count as a price: a model saved at 0 comes back as an empty field
  // and cannot be saved again. See `lib/ai/model-cost.ts`.
  const inputCostMissing = !isPricedCost(formData.cost_input_per_1m_usd)
  const outputCostMissing = !isPricedCost(formData.cost_output_per_1m_usd)

  // Single gate for both Save and Test: block on invalid form, otherwise save.
  const handleSubmit = async (opts?: { keepOpen?: boolean }): Promise<boolean> => {
    setAttemptedSubmit(true)
    if (inputCostMissing || outputCostMissing) return false
    return onSave(opts)
  }

  // "Save & Test" — NOT a read-only probe. It persists the current model form
  // first (keepOpen), then opens the probe dialog against the saved/draft config
  // so the test runs on the on-screen values. The save step also rejects invalid
  // JSON before any probe runs.
  const handleSaveAndTest = async () => {
    setIsPreparingTest(true)
    try {
      if (await handleSubmit({ keepOpen: true })) setTestDialogOpen(true)
    } finally {
      setIsPreparingTest(false)
    }
  }
  // Provider cost estimate (8K input + 2K output tokens)
  const estimatedProviderCost = (() => {
    if (
      typeof formData.cost_input_per_1m_usd !== 'number' ||
      typeof formData.cost_output_per_1m_usd !== 'number' ||
      formData.cost_input_per_1m_usd <= 0
    ) return null
    const inputMicro = Math.round(formData.cost_input_per_1m_usd * 1_000_000)
    const outputMicro = Math.round((formData.cost_output_per_1m_usd || 0) * 1_000_000)
    return (8_000 * inputMicro + 2_000 * outputMicro) / 1_000_000
  })()

  // Can test if we have model name and provider
  const canTest = formData.model_name && providerId

  // On-screen params for the test probe. Only valid plain-object JSON is passed;
  // invalid JSON yields undefined so the probe never runs against a silent {}
  // (Save & Test persists first, and that save already rejects invalid JSON).
  const testConfig = ((): Record<string, unknown> | undefined => {
    if (!formData.config_json?.trim()) return {}
    try {
      const parsed = JSON.parse(formData.config_json)
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : undefined
    } catch {
      return undefined
    }
  })()

  // Example params for the config editor. LangChain expects camelCase keys and
  // they differ per provider (e.g. OpenAI maxTokens vs Google maxOutputTokens);
  // unknown keys are silently ignored by the provider client.
  const configPlaceholder =
    providerKey === 'google'
      ? '{"temperature": 0.7, "maxOutputTokens": 1024}'
      : '{"temperature": 0.7, "maxTokens": 1024}'
  const docLinks = getProviderDocLinks(providerKey)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <span key={editingModel ? tUi("editModel") : tUi("addModel")}>{editingModel ? tUi("editModel") : tUi("addModel")}</span>
          </DialogTitle>
          <DialogDescription>
            <span key={editingModel
              ? tUi("updateModelConfigurationForValue0", { value0: String(providerName) })
              : tUi("addANewModelForValue0", { value0: String(providerName) })}>{editingModel
              ? tUi("updateModelConfigurationForValue0", { value0: String(providerName) })
              : tUi("addANewModelForValue0", { value0: String(providerName) })}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="model_name">
                <span><span key={tUi("modelID")}>{tUi("modelID")}</span></span> <span className="text-red-500">*</span>
              </Label>
              <Input
                id="model_name"
                placeholder={"gpt-4o-mini"}
                value={formData.model_name}
                onChange={(e) =>
                  onChange({ ...formData, model_name: e.target.value })
                }
              />
              <p className="text-xs text-gray-500">
                <span><span key={tUi("exactModelNameForAPICalls")}>{tUi("exactModelNameForAPICalls")}</span></span>
                {docLinks && (
                  <>
                    {' · '}
                    <a
                      href={docLinks.models}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-blue-600 hover:underline"
                    >
                      <span><span key={tUi("availableModels")}>{tUi("availableModels")}</span></span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_name">
                <span><span key={tUi("displayName")}>{tUi("displayName")}</span></span> <span className="text-red-500">*</span>
              </Label>
              <Input
                id="display_name"
                placeholder={"GPT-4o Mini"}
                value={formData.display_name}
                onChange={(e) =>
                  onChange({ ...formData, display_name: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model_type">
              <span><span key={tUi("modelType")}>{tUi("modelType")}</span></span> <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.model_type}
              onValueChange={(value: ModelType) =>
                onChange({ ...formData, model_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={tUi("selectModelType")} />
              </SelectTrigger>
              <SelectContent>
                {MODEL_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.model_type === 'embedding' && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span><TranslationText>{tUi.rich("theKBVectorStoreIsCurrentlyLockedTo", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>), tag2: chunks => (<code>{chunks}</code>), tag3: chunks => (<code>{chunks}</code>), tag4: chunks => (<code>{chunks}</code>), tag5: chunks => (<code>{chunks}</code>), tag6: chunks => (<code>{chunks}</code>) })}</TranslationText></span>
              </div>
            )}
          </div>

          {/* Capabilities */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-sm text-gray-700 mb-3">
              <span key={tUi("capabilities")}>{tUi("capabilities")}</span></h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="supports_streaming" className="cursor-pointer">
                  <span key={tUi("supportsStreaming")}>{tUi("supportsStreaming")}</span></Label>
                <Switch
                  id="supports_streaming"
                  checked={formData.supports_streaming}
                  onCheckedChange={(checked) =>
                    onChange({ ...formData, supports_streaming: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label
                  htmlFor="supports_function_calling"
                  className="cursor-pointer"
                >
                  <span key={tUi("supportsFunctionCallingTools")}>{tUi("supportsFunctionCallingTools")}</span></Label>
                <Switch
                  id="supports_function_calling"
                  checked={formData.supports_function_calling}
                  onCheckedChange={(checked) =>
                    onChange({ ...formData, supports_function_calling: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="supports_vision" className="cursor-pointer">
                  <span key={tUi("supportsVisionImages")}>{tUi("supportsVisionImages")}</span></Label>
                <Switch
                  id="supports_vision"
                  checked={formData.supports_vision}
                  onCheckedChange={(checked) =>
                    onChange({ ...formData, supports_vision: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Default Configuration (JSON) */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-sm text-gray-700 mb-3">
              <span key={tUi("defaultConfigurationJSON")}>{tUi("defaultConfigurationJSON")}</span></h4>
            <Textarea
              id="config_json"
              rows={6}
              className="font-mono text-sm"
              placeholder={configPlaceholder}
              value={formData.config_json}
              onChange={(e) =>
                onChange({ ...formData, config_json: e.target.value })
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              <span><span key={tUi("defaultParametersForThisModelUsedAsA")}>{tUi("defaultParametersForThisModelUsedAsA")}</span></span>
              {docLinks && (
                <><TranslationText>{tUi.rich("commonTag0Tag0", { tag0: () => (<ParamKeys keys={docLinks.commonParams} />) })}</TranslationText></>
              )}
              <span><span key={tUi("unknownKeysAreIgnored")}>{tUi("unknownKeysAreIgnored")}</span></span>
            </p>
          </div>

          {/* Pricing */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-sm text-gray-700 mb-3">
              <span key={tUi("pricingPer1MTokensUSD")}>{tUi("pricingPer1MTokensUSD")}</span></h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cost_input_per_1m_usd">
                  <span><span key={tUi("inputCost")}>{tUi("inputCost")}</span></span> <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="cost_input_per_1m_usd"
                  type="number"
                  step="0.01"
                  placeholder="0.15"
                  aria-invalid={attemptedSubmit && inputCostMissing}
                  value={formData.cost_input_per_1m_usd}
                  onChange={(e) =>
                    onChange({
                      ...formData,
                      cost_input_per_1m_usd: e.target.value
                        ? parseFloat(e.target.value)
                        : '',
                    })
                  }
                />
                {attemptedSubmit && inputCostMissing && (
                  <p className="text-xs text-red-500"><span key={tUi("inputCostIsRequiredAndMustBeGreater")}>{tUi("inputCostIsRequiredAndMustBeGreater")}</span></p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost_output_per_1m_usd">
                  <span><span key={tUi("outputCost")}>{tUi("outputCost")}</span></span> <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="cost_output_per_1m_usd"
                  type="number"
                  step="0.01"
                  placeholder="0.60"
                  aria-invalid={attemptedSubmit && outputCostMissing}
                  value={formData.cost_output_per_1m_usd}
                  onChange={(e) =>
                    onChange({
                      ...formData,
                      cost_output_per_1m_usd: e.target.value
                        ? parseFloat(e.target.value)
                        : '',
                    })
                  }
                />
                {attemptedSubmit && outputCostMissing && (
                  <p className="text-xs text-red-500"><span key={tUi("outputCostIsRequiredAndMustBeGreater")}>{tUi("outputCostIsRequiredAndMustBeGreater")}</span></p>
                )}
              </div>
            </div>

            {estimatedProviderCost !== null && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
                <Zap className="h-4 w-4 shrink-0" />
                <span><TranslationText>{tUi.rich("value0ProviderCostPerTypicalRequest8KIn", { value0: String(formatMicroUsdPrecise(estimatedProviderCost)) })}</TranslationText></span>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="is_enabled" className="cursor-pointer">
                <span key={tUi("enableModel")}>{tUi("enableModel")}</span></Label>
              <Switch
                id="is_enabled"
                checked={formData.is_enabled}
                onCheckedChange={(checked) =>
                  onChange({ ...formData, is_enabled: checked })
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          {editingModel && onDelete && (
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={isSaving}
              className="mr-auto"
            >
              <span key={tUi("delete")}>{tUi("delete")}</span></Button>
          )}
          {canTest && (
            <Button
              variant="outline"
              onClick={handleSaveAndTest}
              disabled={isSaving}
            >
              {isPreparingTest ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              <span key={isPreparingTest ? tUi("savingTesting") : tUi("saveTest")}>{isPreparingTest ? tUi("savingTesting") : tUi("saveTest")}</span>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={() => handleSubmit()} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
            ) : (
              tUi("save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Test Dialog */}
      <ModelTestDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        model={editingModel || undefined}
        formData={
          !editingModel && canTest
            ? {
                providerId,
                modelName: formData.model_name,
                modelType: formData.model_type,
                supportsFunctionCalling: formData.supports_function_calling,
              }
            : undefined
        }
        config={testConfig}
        providerName={providerName}
      />
    </Dialog>
  )
}
